import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

import { boundToYmd, isYmd, pickInterval } from './dateRangeBound'

/**
 * Date-range picker with an automatically-derived histogram interval — the
 * declarative replacement for the bespoke date+interval logic that used to
 * live inside the WsolutionDashboard React component.
 *
 * Two free `<input type="date">` (like DateRangeFacet) write a `{gte, lte}`
 * range to `dataLocation` (the shape the backend `range` filter understands).
 * On every change it ALSO:
 *   - derives a sensible ES `fixed_interval` from the span (15m…7d) and writes
 *     it to `intervalLocation` (for an instant "pas de temps" label), and
 *   - assembles the query URL by substituting `__INTERVAL__` in `urlTemplate`
 *     and writes it to `urlLocation`, so the page's `submitData` action can
 *     POST to it with `url: ~~.<urlLocation>` (reactive-json URLs are a single
 *     ref and can't concat the interval client-side).
 *
 * The refetch itself lives in the YAML `actions:` (fired `on: change`, like the
 * faceted-search kit) — this primitive only prepares the store then lets the
 * bubbled event trigger the page's submitData.
 *
 * YAML usage:
 *   - type: DateRangeAutoInterval
 *     dataLocation: ~~.filter.destination_response_start_time
 *     intervalLocation: ~~.interval
 *     urlLocation: ~~.esQueryUrl
 *     urlTemplate: "/api/.../wsolution_dashboard?wsid=XXX&interval=__INTERVAL__"
 *     actions:
 *       - { what: setData, on: change, path: ~~.esLoading, value: true }
 *       - { what: submitData, on: change, url: ~~.esQueryUrl, httpMethod: POST, ... }
 */

const DAY_MS = 86_400_000

// Default span→interval breakpoints (first match wins): finer buckets for
// short ranges, coarser for long ones. Read by `pickInterval()`, which lives
// in dateRangeBound.js with the other pure period helpers.
//
// Every step is sized to yield at most ~96 points, so resolution degrades
// smoothly with the span. The daily step runs to a quarter on purpose : the
// previous table handed 1d to 31 days and jumped straight to 7d, so asking
// for 39 days collapsed a chart to THREE points — and a day with 19 calls
// was folded into a weekly bucket beside one with 6 117, i.e. invisible.
const DEFAULT_BREAKPOINTS = [
  { maxDays: 1, interval: '15m' },
  { maxDays: 2, interval: '1h' },
  { maxDays: 7, interval: '3h' },
  { maxDays: 92, interval: '1d' },
  { maxDays: Infinity, interval: '7d' },
]

function toYmd(date) {
  return date.toISOString().slice(0, 10)
}

export const DateRangeAutoInterval = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const fromSuffix = resolve(props.fromSuffix) ?? 'T00:00:00'
  const toSuffix = resolve(props.toSuffix) ?? 'T23:59:59.999'
  const defaultDays = Number(resolve(props.defaultDays)) || 30
  const urlTemplate = resolve(props.urlTemplate) ?? ''
  const breakpoints = Array.isArray(resolve(props.breakpoints))
    ? resolve(props.breakpoints)
    : DEFAULT_BREAKPOINTS
  const inputClass = resolve(props.inputClass)
    ?? 'border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: {},
    globalDataContext,
    templateContext,
  })
  const range = formData && typeof formData === 'object' ? formData : {}

  // Resolve where to write the derived interval + assembled URL. Both are
  // optional (a page may only need the URL), so guard before writing.
  const intervalPath = resolvePath(props.intervalLocation, { path, datafield, globalDataContext, templateContext })
  const urlPath = resolvePath(props.urlLocation, { path, datafield, globalDataContext, templateContext })

  // ISO string → YYYY-MM-DD; fall back to a sensible default window so the
  // inputs show the same range the initial (baked) data load used.
  // ISO ou date-math (`now-30d`) → YYYY-MM-DD, cf. dateRangeBound.js.
  const ymd = (value) => boundToYmd(value)
  const defaultFrom = toYmd(new Date(Date.now() - defaultDays * DAY_MS))
  const defaultTo = toYmd(new Date())
  const fromVal = ymd(range.gte) || defaultFrom
  const toVal = ymd(range.lte) || defaultTo

  const write = (fromYmd, toYmd2) => {
    // An `<input type="date">` reports an EMPTY value for as long as the day,
    // month and year segments aren't all filled, so a date typed by hand goes
    // through that state on every keystroke. Writing then put a range with NO
    // lower bound in the store — everything before the upper one — and, the
    // span being unreadable, the coarsest interval ; the `change` event
    // carried both straight into the refetch. A half-written date says
    // nothing about the period the reader wants : wait for the one they
    // finish.
    if (!isYmd(fromYmd) || !isYmd(toYmd2)) return

    globalDataContext.updateData(
      { gte: `${fromYmd}${fromSuffix}`, lte: `${toYmd2}${toSuffix}` },
      formDataPath,
    )

    const interval = pickInterval(
      new Date(`${fromYmd}T00:00:00`),
      new Date(`${toYmd2}T23:59:59.999`),
      breakpoints,
    )
    if (intervalPath) globalDataContext.updateData(interval, intervalPath)
    if (urlPath) globalDataContext.updateData(urlTemplate.split('__INTERVAL__').join(interval), urlPath)
  }

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef} className="flex items-center gap-2">
        <input
          type="date"
          aria-label={resolve(props.fromLabel) ?? 'Début de la période'}
          value={fromVal}
          max={toVal || undefined}
          onChange={(e) => write(e.target.value, toVal)}
          className={inputClass}
        />
        <span className="text-gray-400 text-sm">→</span>
        <input
          type="date"
          aria-label={resolve(props.toLabel) ?? 'Fin de la période'}
          value={toVal}
          min={fromVal || undefined}
          onChange={(e) => write(fromVal, e.target.value)}
          className={inputClass}
        />
      </div>
    </ActionDependant>
  )
}

/** Resolves a `dataLocation`-style prop to a writable store path, or null. */
function resolvePath(dataLocation, { path, datafield, globalDataContext, templateContext }) {
  if (!dataLocation) return null
  const { formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation,
    defaultValue: null,
    globalDataContext,
    templateContext,
  })
  return formDataPath
}
